/**
 * Zustand slice for the in-app chat assistant.
 *
 * R-143-CHAT-ASSISTANT Phase A.
 *
 * Lives alongside the existing slices in `src/store/`. Combined into
 * the root store via the standard `(...args) => ({ ...createChatSlice(...args), ... })`
 * pattern.
 *
 * State held here is in-flight session data:
 *   - whether the chat panel is open
 *   - the active conversation id + the message buffer
 *   - the explicit site scope (overrides the route-detected default)
 *
 * Persisted conversations live in Firestore under
 * `users/{uid}/conversations/{id}`; this slice only mirrors the active
 * one for UI rendering.
 */
import type { StateCreator } from 'zustand';

import type { ChatConversation, ChatMessage, ChatMessageFeedback } from '../types/chat';

export interface ChatSliceState {
  isOpen: boolean;
  /** When non-null, overrides the route-derived site scope. */
  scopeOverride: string | null | undefined;
  activeConversation: ChatConversation | null;
  /** Inline composer text — survives panel close/reopen. */
  composerDraft: string;
  /** Last in-flight asyncroundtrip — UI shows the typing indicator. */
  isWaiting: boolean;
}

export interface ChatSliceActions {
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  /**
   * Explicitly scope the chat. Pass `null` for portfolio-wide;
   * pass `undefined` to clear the override and fall back to the
   * route-detected default.
   */
  setScopeOverride: (scope: string | null | undefined) => void;
  setComposerDraft: (text: string) => void;
  startNewConversation: (uid: string, modelId: string, siteId: string | null) => void;
  appendMessage: (msg: ChatMessage) => void;
  setFeedback: (messageId: string, feedback: ChatMessageFeedback) => void;
  markCuratedInsight: (messageId: string, insightId: string) => void;
  setWaiting: (waiting: boolean) => void;
  hydrateActiveConversation: (conv: ChatConversation) => void;
  resetChatSession: () => void;
}

export type ChatSlice = ChatSliceState & ChatSliceActions;

const initialState: ChatSliceState = {
  isOpen: false,
  scopeOverride: undefined,
  activeConversation: null,
  composerDraft: '',
  isWaiting: false,
};

function newConversationId(): string {
  // Use crypto.randomUUID where available; fall back to Math.random for older
  // jsdom test envs.
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `conv-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export const createChatSlice: StateCreator<ChatSlice, [], [], ChatSlice> = (set) => ({
  ...initialState,

  openChat: () => set({ isOpen: true }),
  closeChat: () => set({ isOpen: false }),
  toggleChat: () => set((s) => ({ isOpen: !s.isOpen })),

  setScopeOverride: (scope) => set({ scopeOverride: scope }),

  setComposerDraft: (text) => set({ composerDraft: text }),

  startNewConversation: (uid, modelId, siteId) => {
    const now = new Date().toISOString();
    const conv: ChatConversation = {
      id: newConversationId(),
      uid,
      title: 'New conversation',
      siteId,
      messages: [],
      modelId,
      createdAt: now,
      updatedAt: now,
    };
    set({ activeConversation: conv, composerDraft: '', isWaiting: false });
  },

  appendMessage: (msg) =>
    set((s) => {
      if (!s.activeConversation) return s;
      // First user message titles the conversation.
      const isFirstUserMessage =
        msg.role === 'user' && s.activeConversation.messages.every((m) => m.role !== 'user');
      return {
        activeConversation: {
          ...s.activeConversation,
          messages: [...s.activeConversation.messages, msg],
          title: isFirstUserMessage
            ? msg.content.slice(0, 80).trim() || s.activeConversation.title
            : s.activeConversation.title,
          updatedAt: msg.createdAt,
        },
      };
    }),

  setFeedback: (messageId, feedback) =>
    set((s) => {
      if (!s.activeConversation) return s;
      return {
        activeConversation: {
          ...s.activeConversation,
          messages: s.activeConversation.messages.map((m) =>
            m.id === messageId ? { ...m, feedback } : m,
          ),
        },
      };
    }),

  markCuratedInsight: (messageId, insightId) =>
    set((s) => {
      if (!s.activeConversation) return s;
      return {
        activeConversation: {
          ...s.activeConversation,
          messages: s.activeConversation.messages.map((m) =>
            m.id === messageId ? { ...m, curatedInsightId: insightId } : m,
          ),
        },
      };
    }),

  setWaiting: (waiting) => set({ isWaiting: waiting }),

  hydrateActiveConversation: (conv) =>
    set({ activeConversation: conv, isWaiting: false, composerDraft: '' }),

  resetChatSession: () => set({ ...initialState }),
});
